/**
 * Image URL Utility
 * Centralized function to construct image URLs using cdnBaseUrl from config service
 * 
 * This ensures all image URLs use the CDN endpoint provided by the backend config API
 * instead of hardcoded S3 URLs.
 */

import configService from '../services/configService';

/**
 * Get full image URL from image path/key
 * 
 * @param imagePath - S3 key or path (e.g., "weeklyReports/01-01-2025/image.jpg")
 *                    Can also be a full URL, blob URL, or null/undefined
 * @returns Full URL to the image using cdnBaseUrl from config
 * 
 * @example
 * getImageUrl("weeklyReports/01-01-2025/image.jpg")
 * // Returns: "https://cdn-dev.sportsforlife.in/weeklyReports/01-01-2025/image.jpg"
 * 
 * getImageUrl("https://example.com/image.jpg")
 * // Returns: "https://example.com/image.jpg" (unchanged)
 */
export function getImageUrl(imagePath: string | null | undefined): string {
  // Handle null/undefined/empty
  if (!imagePath) {
    return '';
  }

  // Filter out "string" placeholder value from backend
  if (imagePath === 'string' || imagePath.trim() === 'string') {
    return '';
  }

  // If already a full URL (http/https), return as-is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  // If blob URL (for previews), return as-is
  if (imagePath.startsWith('blob:')) {
    return imagePath;
  }

  // If data URL (base64), return as-is
  if (imagePath.startsWith('data:')) {
    return imagePath;
  }

  // Get CDN base URL from config service
  const cdnBaseUrl = configService.getCdnBaseUrl();
  
  // Ensure cdnBaseUrl doesn't end with slash and imagePath doesn't start with slash
  const cleanBaseUrl = cdnBaseUrl.endsWith('/') 
    ? cdnBaseUrl.slice(0, -1) 
    : cdnBaseUrl;
  
  const cleanPath = imagePath.startsWith('/') 
    ? imagePath.slice(1) 
    : imagePath;

  // Construct full URL
  return `${cleanBaseUrl}/${cleanPath}`;
}

/**
 * Get image URL with fallback
 * 
 * @param imagePath - S3 key or path
 * @param fallback - Fallback URL if imagePath is empty
 * @returns Full URL or fallback
 */
export function getImageUrlWithFallback(
  imagePath: string | null | undefined,
  fallback: string
): string {
  const url = getImageUrl(imagePath);
  return url || fallback;
}

